var _a;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect.js';
import { getDocument } from '../functions/dom.js';
import { ThemeContext } from '../../ThemeProvider/ThemeProvider.js';
import * as React from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- only gets created at build-time
// @ts-ignore
import allCss, { revertV1Css } from '../../../styles.js';
// react <18 fallback for useInsertionEffect, with workaround for webpack getting rid of React namespace
const _React = React;
const useIsomorphicInsertionEffect = (_a = _React.useInsertionEffect) !== null && _a !== void 0 ? _a : useIsomorphicLayoutEffect;
/** This lives outside the hook because we only want to load styles once even when context is not provided. */
let globalLoaded = false;
/**
 * Dynamically loads the iTwinUI styles as a constructed stylesheet,
 * falling back to a regular `<style>` tag in `<head>`.
 */
export const useStyles = (options) => {
    const context = _React.useContext(ThemeContext);
    useIsomorphicInsertionEffect(() => {
        var _a, _b, _c;
        const includeCss = (_b = (_a = options === null || options === void 0 ? void 0 : options.includeCss) !== null && _a !== void 0 ? _a : context === null || context === void 0 ? void 0 : context.includeCss) !== null && _b !== void 0 ? _b : { withLayer: true };
        if ((context === null || context === void 0 ? void 0 : context.stylesLoaded.current) || globalLoaded || !includeCss) {
            return;
        }
        const withLayer = typeof includeCss === 'object' ? includeCss === null || includeCss === void 0 ? void 0 : includeCss.withLayer : false;
        const document = (_c = options === null || options === void 0 ? void 0 : options.document) !== null && _c !== void 0 ? _c : (() => { var _a, _b; return (_b = (_a = context === null || context === void 0 ? void 0 : context.rootRef.current) === null || _a === void 0 ? void 0 : _a.ownerDocument) !== null && _b !== void 0 ? _b : getDocument(); });
        loadStyles({ withLayer, document });
        if (context) {
            context.stylesLoaded.current = true;
        }
        else {
            globalLoaded = true;
        }
    }, [context, options]);
};
// ----------------------------------------------------------------------------
const layers = `@layer itwinui-v1, itwinui.v1, itwinui.v2, itwinui.v3;`;
const loadStyles = ({ withLayer = true, document = () => getDocument() }) => {
    var _a;
    /* eslint-disable @typescript-eslint/no-explicit-any -- a lot of things inside this function don't have proper types */
    const _document = ((_a = (typeof document === 'function' ? document() : document)) !== null && _a !== void 0 ? _a : getDocument());
    if (typeof jest !== 'undefined' || typeof _document === 'undefined') {
        return;
    }
    if ((_document === null || _document === void 0 ? void 0 : _document.documentElement).iuiv2Loaded) {
        return;
    }
    const cssText = withLayer
        ? `@charset "utf-8";\n${layers}\n${revertV1Css}\n@layer itwinui.v2 { ${allCss} }`
        : `@charset "utf-8";\n${revertV1Css}\n${allCss}`;
    const supportsAdopting = 'adoptedStyleSheets' in Document.prototype &&
        'replaceSync' in CSSStyleSheet.prototype;
    if (supportsAdopting && _document.defaultView) {
        const sheet = new _document.defaultView.CSSStyleSheet();
        sheet.replaceSync(cssText);
        _document.adoptedStyleSheets = [..._document.adoptedStyleSheets, sheet];
    }
    else {
        const style = _document.createElement('style');
        style.setAttribute('data-iui-styles', '');
        style.textContent = cssText;
        _document.head.appendChild(style);
    }
    _document.documentElement.iuiv2Loaded = true;
    /* eslint-enable @typescript-eslint/no-explicit-any */
};
